import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LumiqoLogo } from "@/components/lumiqo/Logo";
import { SOCIAL_ICONS } from "@/components/lumiqo/SocialIcons";
import { NAV_LINKS, SOCIAL_LINKS } from "@/lib/lumiqoData";

const Navbar = ({ onCTAClick }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleLink = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "py-2 backdrop-blur-xl bg-white/75 border-b border-[#0a1d4d]/10"
          : "py-3 sm:py-4 bg-transparent"
      }`}
      data-testid="lumiqo-navbar"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 flex items-center justify-between gap-4">
        <button
          onClick={() => handleLink("#top")}
          className="flex items-center shrink-0 -my-1"
          data-testid="navbar-logo-button"
          aria-label="Lumiqo home"
        >
          <LumiqoLogo className="h-11 md:h-14 lg:h-16" />
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((link) => (
            <button
              key={link.href}
              onClick={() => handleLink(link.href)}
              className="text-sm font-medium text-[#0a1d4d]/75 hover:text-[#1d72ff] transition-colors"
              data-testid={`nav-link-${link.label.toLowerCase()}`}
            >
              {link.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <div className="flex items-center gap-1 mr-1">
            {SOCIAL_LINKS.map(({ label, href, icon }) => {
              const Icon = SOCIAL_ICONS[icon];
              return (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="w-9 h-9 rounded-full flex items-center justify-center text-[#0a1d4d]/65 hover:text-[#1d72ff] hover:bg-[#1d72ff]/8 transition-colors duration-300"
                  data-testid={`navbar-social-${label.toLowerCase()}`}
                >
                  <Icon className="w-[18px] h-[18px]" />
                </a>
              );
            })}
          </div>
          <Button
            onClick={onCTAClick}
            className="rounded-full bg-[#1d72ff] hover:bg-[#0a1d4d] text-white font-medium px-5 h-10 transition-colors duration-300 group"
            data-testid="navbar-cta-button"
          >
            Get Started
            <ArrowRight className="ml-1.5 w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </Button>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          className="md:hidden p-2 text-[#0a1d4d]"
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="md:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-t border-[#0a1d4d]/10"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-5 flex flex-col gap-3">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleLink(link.href)}
                  className="text-left text-base font-medium text-[#0a1d4d] py-2 border-b border-[#0a1d4d]/5"
                  data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </button>
              ))}
              <Button
                onClick={() => {
                  setOpen(false);
                  onCTAClick();
                }}
                className="mt-2 rounded-full bg-[#1d72ff] hover:bg-[#0a1d4d] text-white font-medium h-11"
                data-testid="mobile-cta-button"
              >
                Get Started <ArrowRight className="ml-1.5 w-4 h-4" />
              </Button>
              <div className="mt-3 pt-3 border-t border-[#0a1d4d]/5 flex items-center justify-center gap-4">
                {SOCIAL_LINKS.map(({ label, href, icon }) => {
                  const Icon = SOCIAL_ICONS[icon];
                  return (
                    <a
                      key={label}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                      className="w-10 h-10 rounded-full bg-[#f5f6fa] border border-[#0a1d4d]/8 flex items-center justify-center text-[#0a1d4d]/70 hover:text-[#1d72ff] transition-colors"
                      data-testid={`mobile-social-${label.toLowerCase()}`}
                    >
                      <Icon className="w-[18px] h-[18px]" />
                    </a>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
